import React, { useState, useEffect } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import { apiFetch } from '../api';
import {
  emptyContractForm,
  updateContractFormField,
  buildContractPayload,
} from '../utils/contractForm';
import ContractFormFields from './ContractFormFields';

function ContractCreateModal({ show, onHide, productId, onCreated, initialData }) {
  const [form, setForm] = useState(emptyContractForm());
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      if (initialData) {
        const { number, ...rest } = initialData;
        setForm({
          ...rest,
          number: number + '-copy',
        });
      } else {
        setForm(emptyContractForm());
      }
      setError('');
    }
  }, [show, initialData]);

  const handleChange = (field, value) => {
    setForm((prev) => updateContractFormField(prev, field, value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // 1. Создаём новый договор (без файлов)
      const payload = buildContractPayload(form, { productId });
      const newContract = await apiFetch('/api/contracts/', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const newContractId = newContract.id;

      // 2. Если есть исходный договор – копируем все его файлы
      if (initialData) {
        // Получаем список файлов исходного договора
        const filesResponse = await apiFetch(`/api/contract-files/?contract=${initialData.id}`);
        const files = filesResponse.results || filesResponse;

        // Для каждого файла скачиваем и загружаем к новому договору
        for (const fileObj of files) {
          const fileUrl = fileObj.file;          // URL файла на сервере
          const fileName = fileObj.name || fileUrl.split('/').pop() || 'file';

          try {
            // Скачиваем файл
            const response = await fetch(fileUrl);
            if (!response.ok) {
              console.warn('Не удалось скачать файл', fileUrl);
              continue;
            }
            const blob = await response.blob();
            const file = new File([blob], fileName, { type: blob.type || 'application/octet-stream' });

            // Загружаем файл к новому договору
            const formData = new FormData();
            formData.append('file', file);
            formData.append('contract', newContractId);
            // Если бэкенд ожидает поле name, передаём его
            formData.append('name', fileName);

            await apiFetch('/api/contract-files/', {
              method: 'POST',
              body: formData,
            });
          } catch (copyErr) {
            console.error('Ошибка копирования файла:', copyErr);
            // Продолжаем с остальными файлами
          }
        }
      }

      // 3. Уведомляем родителя о создании
      onCreated(newContract);
      onHide();
    } catch (err) {
      setError(err.message || 'Ошибка создания договора');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="app-surface">
        <Modal.Title>{initialData ? 'Копировать договор' : 'Новый договор'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="app-surface">
          {error && <Alert variant="danger">{error}</Alert>}
          <ContractFormFields form={form} onChange={handleChange} />
          <p className="app-text-muted small mt-3 mb-0">
            {initialData
              ? 'Будут скопированы все файлы и изображения.'
              : 'При создании автоматически добавятся все стадии производства по шаблону.'}
          </p>
        </Modal.Body>
        <Modal.Footer className="app-surface">
          <button type="button" className="app-btn" onClick={onHide}>Отмена</button>
          <button type="submit" className="app-btn app-btn-primary" disabled={saving}>
            {saving ? 'Сохранение...' : initialData ? 'Копировать' : 'Создать договор'}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default ContractCreateModal;